import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import { analysisAPI, kpisAPI, commentsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import {
  Target,
  Edit,
  BarChart3,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ArrowLeft,
  Download,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  GitBranch,
  Layers,
  MessageCircle,
  Info,
  Zap,
  User,
  Ban,
  Undo2,
  Lock,
} from 'lucide-react';
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { toast } from 'sonner';
import { UserAvatar } from '../UserAvatar';
import { 
  cn, 
  formatNumber,
  formatAxisNumber,
  getBimesterName,
  getGoalPeriodName,
  getGoalPeriodCount,
  getGoalPeriodTermLabel,
  calculateKPIStatus, 
  getStatusText,
  calculateKPISummary,
  calculateOverallPerformance,
  calculateRangeOverall,
  formatGoalRange,
  STATUS_CONFIG,
  formatDate,
  isPeriodEditable,
} from '../../lib/utils';

// ============================================
// CONSTANTS
// ============================================
const STATUS_ICON_MAP = {
  success: CheckCircle2,
  error: XCircle,
  pending: Clock,
};

// Comment label configuration
const COMMENT_LABELS = {
  informativo: { label: 'Informativo', icon: Info, color: 'bg-blue-100 text-blue-700 border-blue-300' },
  desviacion: { label: 'Desviación', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 border-amber-300' },
  accion_tomada: { label: 'Plan de Acción', icon: Zap, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

// Comment type configuration
const COMMENT_TYPES = {
  comment: { label: 'Comentario', icon: MessageCircle, color: 'text-slate-500' },
  result_correction: { label: 'Corrección de resultado', icon: Edit, color: 'text-orange-600' },
  goal_correction: { label: 'Corrección de meta', icon: Target, color: 'text-purple-600' },
};

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * Renders a status badge with icon and text
 * Uses centralized STATUS_CONFIG from utils
 */
const StatusBadge = ({ status, isRange = false }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const label = isRange && status === 'success' ? 'En rango'
    : isRange && status === 'error' ? 'Fuera de rango'
    : config.label;
  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium border', config.badgeClass)}>
      {label}
    </span>
  );
};

/**
 * Mini status badge for hierarchy nodes in tables
 */
const NodeStatusBadge = ({ result, goal, evaluationType, compact = false }) => {
  const status = calculateKPIStatus(result, goal, evaluationType);
  
  if (status === 'pending') return null;
  
  const config = STATUS_CONFIG[status];
  const Icon = STATUS_ICON_MAP[status];
  
  if (compact) {
    return (
      <span className={cn('inline-flex items-center justify-center w-5 h-5 rounded-full', config.bgColor)}>
        <Icon className={cn('w-3 h-3', config.iconColor)} />
      </span>
    );
  }
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
      config.badgeClass
    )}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

/**
 * Goal cell - simple styling
 */
const GoalCell = ({ value, className, isTotal = false }) => (
  <td className={cn('text-right', className)}>
    <span className={isTotal ? 'font-semibold' : ''}>
      {formatNumber(value)}
    </span>
  </td>
);

/**
 * Result cell - simple styling with font weight
 */
const ResultCell = ({ value, className, isTotal = false }) => {
  const hasValue = value !== null && value !== undefined;
  
  return (
    <td className={cn('text-right font-medium', className)}>
      <span className={isTotal ? 'font-semibold' : ''}>
        {hasValue ? formatNumber(value) : '-'}
      </span>
    </td>
  );
};

const KPIDetailView = ({ kpiId, isAdmin, onEdit, kpiList = [] }) => {
  const navigate = useNavigate();
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedBimester, setSelectedBimester] = useState(null);
  const [highlightedPeriod, setHighlightedPeriod] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  
  // Chart view mode for hierarchical KPIs: 'general' (consolidated) or 'breakdown' (by node)
  const [chartViewMode, setChartViewMode] = useState('general');
  const [showTrend, setShowTrend] = useState(false);

  // Trend line computation (must be before early returns)
  const computeTrendData = (data) => {
    if (!data || data.length < 2) return data;
    const points = [];
    data.forEach((d, i) => {
      if (d.resultado != null) points.push({ x: i, y: d.resultado });
    });
    if (points.length < 2) return data;
    const n = points.length;
    const sumX = points.reduce((s, p) => s + p.x, 0);
    const sumY = points.reduce((s, p) => s + p.y, 0);
    const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
    const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
    const denom = n * sumX2 - sumX * sumX;
    if (denom === 0) return data;
    const m = (n * sumXY - sumX * sumY) / denom;
    const b = (sumY - m * sumX) / n;
    return data.map((d, i) => ({ ...d, tendencia: parseFloat((m * i + b).toFixed(2)) }));
  };
  
  // Load saved chart view preference when kpiId changes
  useEffect(() => {
    if (kpiId) {
      const saved = localStorage.getItem(`kpi_chart_view_${kpiId}`);
      if (saved && (saved === 'general' || saved === 'breakdown')) {
        setChartViewMode(saved);
      } else {
        setChartViewMode('general'); // Default to general
      }
    }
  }, [kpiId]);
  
  // Handler to change view mode and save preference
  const handleChartViewChange = (newMode) => {
    setChartViewMode(newMode);
    if (kpiId) {
      localStorage.setItem(`kpi_chart_view_${kpiId}`, newMode);
    }
  };
  
  // Comment history drawer state
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [commentDrawerData, setCommentDrawerData] = useState({ 
    periodId: null, 
    periodNumber: null,
    nodeId: null,
    nodeName: null,
    comments: [],
    loading: false 
  });
  const [commentsSummary, setCommentsSummary] = useState({});
  
  // New comment form state
  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentLabel, setNewCommentLabel] = useState('informativo');
  const [addingComment, setAddingComment] = useState(false);

  // Navigation logic - find current index and determine prev/next
  const currentIndex = useMemo(() => {
    if (!kpiList.length) return -1;
    return kpiList.findIndex(kpi => kpi.id === kpiId);
  }, [kpiList, kpiId]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < kpiList.length - 1;
  
  const previousKPI = hasPrevious ? kpiList[currentIndex - 1] : null;
  const nextKPI = hasNext ? kpiList[currentIndex + 1] : null;

  const handleNavigateToPrevious = () => {
    if (previousKPI) {
      navigate(`/kpis/${previousKPI.id}`);
    }
  };

  const handleNavigateToNext = () => {
    if (nextKPI) {
      navigate(`/kpis/${nextKPI.id}`);
    }
  };

  useEffect(() => {
    const fetchKPIAnalysis = async (id) => {
      try {
        const [kpiRes, analysisRes] = await Promise.all([
          kpisAPI.getById(id),
          analysisAPI.getKPIAnalysis(id, { year: selectedYear, bimester: selectedBimester }),
        ]);
        setSelectedKPI(kpiRes.data);
        setAnalysisData(analysisRes.data);
      } catch (error) {
        console.error('Error fetching KPI analysis:', error);
        toast.error('Error al cargar el análisis');
      }
    };
    
    if (kpiId) {
      fetchKPIAnalysis(kpiId);
    }
  }, [kpiId, selectedYear, selectedBimester]);

  // Fetch comments summary when KPI changes
  useEffect(() => {
    const fetchCommentsSummary = async () => {
      if (!kpiId) return;
      try {
        const response = await commentsAPI.getSummary(kpiId);
        setCommentsSummary(response.data || {});
      } catch (error) {
        console.error('Error fetching comments summary:', error);
      }
    };
    
    fetchCommentsSummary();
  }, [kpiId]);

  // Get period data for drawer header (result and goal)
  const getPeriodData = (periodId) => {
    if (!analysisData?.periods) return null;
    return analysisData.periods.find(p => p.period_id === periodId);
  };

  const handleOpenCommentHistory = async (periodId, periodNumber, nodeId = null, nodeName = null, isLeafNode = true) => {
    // Reset form when opening drawer
    setNewCommentText('');
    setNewCommentLabel('informativo');
    
    setCommentDrawerData({ 
      periodId, 
      periodNumber, 
      nodeId, 
      nodeName,
      isLeafNode,
      comments: [], 
      loading: true 
    });
    setCommentDrawerOpen(true);
    
    try {
      const response = await commentsAPI.getAll({
        kpi_id: kpiId,
        period_id: periodId,
        node_id: nodeId,
      });
      setCommentDrawerData(prev => ({ 
        ...prev, 
        comments: response.data || [], 
        loading: false 
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Error al cargar los comentarios');
      setCommentDrawerData(prev => ({ ...prev, loading: false }));
    }
  };

  // Refresh comments after adding a new one
  const refreshComments = async () => {
    if (!commentDrawerData.periodId) return;
    
    try {
      const response = await commentsAPI.getAll({
        kpi_id: kpiId,
        period_id: commentDrawerData.periodId,
        node_id: commentDrawerData.nodeId,
      });
      setCommentDrawerData(prev => ({ 
        ...prev, 
        comments: response.data || []
      }));
      
      // Also refresh the summary for badge counts
      const summaryResponse = await commentsAPI.getSummary(kpiId);
      setCommentsSummary(summaryResponse.data || {});
    } catch (error) {
      console.error('Error refreshing comments:', error);
    }
  };

  // Anular / reactivar un comentario (solo admin)
  const [togglingId, setTogglingId] = useState(null);
  const handleToggleCancel = async (comment) => {
    setTogglingId(comment.id);
    try {
      await commentsAPI.toggleCancel(comment.id);
      toast.success(comment.estado === 'cancelado' ? 'Comentario reactivado' : 'Comentario anulado');
      await refreshComments();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'No se pudo actualizar el comentario');
    } finally {
      setTogglingId(null);
    }
  };

  // Add new comment from drawer
  const handleAddComment = async () => {
    if (!newCommentText.trim()) {
      toast.error('El comentario no puede estar vacío');
      return;
    }
    
    setAddingComment(true);
    try {
      await commentsAPI.create({
        kpi_id: kpiId,
        period_id: commentDrawerData.periodId,
        node_id: commentDrawerData.nodeId || null,
        text: newCommentText.trim(),
        label: newCommentLabel,
      });
      
      toast.success('Comentario agregado');
      setNewCommentText('');
      setNewCommentLabel('informativo');
      
      // Refresh comments list
      await refreshComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Error al agregar el comentario: ${detail}` : 'Error al agregar el comentario');
    } finally {
      setAddingComment(false);
    }
  };

  // Cierre del drawer con confirmación si hay comentario sin guardar
  const handleDrawerOpenChange = (open) => {
    if (!open && newCommentText.trim()) {
      setCloseConfirmOpen(true);
      return; // no cerrar aún
    }
    setCommentDrawerOpen(open);
  };

  const confirmDrawerClose = () => {
    setCloseConfirmOpen(false);
    setNewCommentText('');
    setCommentDrawerOpen(false);
  };

  const getCommentCount = (periodId, nodeId = null) => {
    const key = nodeId ? `${periodId}_${nodeId}` : periodId;
    return commentsSummary[key] || 0;
  };

  const handleBackToList = () => {
    navigate('/kpis');
  };

  const exportToCSV = () => {
    if (!analysisData?.periods) return;
    
    const headers = ['Periodo', 'Fechas', 'Meta', 'Resultado', 'Desempeño', 'Estado'];
    const rows = analysisData.periods.map((p) => [
      p.period_label || `PER ${p.period_number}`,  // Use period_label from backend
      p.period_dates,
      p.goal,
      p.result ?? '-',
      typeof p.performance === 'number' ? `${p.performance}%` : p.performance || '-',
      p.status || 'pending',
    ]);
    
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(selectedKPI?.name || 'kpi').toUpperCase()}_analisis.csv`;
    a.click();
  };

  if (!selectedKPI) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </Layout>
    );
  }

  // Get periodicity info from analysis data
  const periodicityInfo = analysisData?.periodicity_info || { is_weekly: true, periodicity: 'weekly' };
  const isWeeklyKPI = periodicityInfo.is_weekly;
  const goalPeriodicity = periodicityInfo.goal_periodicity || selectedKPI?.goal_periodicity || 'bimonthly';
  const goalPeriodCount = getGoalPeriodCount(goalPeriodicity);
  
  const chartData = analysisData?.periods?.map((p) => ({
    name: p.period_label || `PER ${p.period_number}`,  // Use period_label from backend
    periodId: p.period_id,
    meta: p.goal,
    resultado: p.result,
    bimester: p.bimester_name,
    rangeMin: p.goal_range?.min ?? null,
    rangeMax: p.goal_range?.max ?? null,
  })) || [];

  // Meta por rango: escalera CONTINUA por bimestre (segmentos planos unidos por un
  // escalón vertical en el cambio de bimestre, sin huecos) que no se extiende más allá
  // del último bucket con rango configurado.
  if ((selectedKPI?.goal_calculation_type === 'range') && chartData.length > 0) {
    // Último índice con rango configurado -> no dibujar más allá (corte al final)
    let lastConfigured = -1;
    chartData.forEach((row, i) => { if (row.rangeMin !== null && row.rangeMin !== undefined) lastConfigured = i; });
    chartData.forEach((row, i) => {
      if (i > lastConfigured) { row.rangeMin = null; row.rangeMax = null; }
    });
  }

  // For hierarchical KPIs with multiple root parents, create chart data with series per parent
  const rootNodes = selectedKPI?.has_hierarchy 
    ? (selectedKPI?.hierarchy_nodes?.filter(n => !n.parent_id) || [])
    : [];
  const hasMultipleRoots = rootNodes.length > 1;
  
  // Colors for different parent series
  const parentColors = [
    { stroke: '#8b5cf6', fill: '#8b5cf6' }, // violet
    { stroke: '#06b6d4', fill: '#06b6d4' }, // cyan
    { stroke: '#f97316', fill: '#f97316' }, // orange
    { stroke: '#ec4899', fill: '#ec4899' }, // pink
    { stroke: '#14b8a6', fill: '#14b8a6' }, // teal
  ];
  
  // Build chart data with parent series if hierarchical with multiple roots
  const hierarchicalChartData = hasMultipleRoots ? analysisData?.periods?.map((p) => {
    const hierarchyPeriod = analysisData?.hierarchy?.periods?.[p.period_id];
    const dataPoint = {
      name: p.period_label || `PER ${p.period_number}`,  // Use period_label from backend
      periodId: p.period_id,
      meta: p.goal, // Same fixed goal for all
    };
    
    // Add result for each root parent
    rootNodes.forEach((root) => {
      const nodeData = hierarchyPeriod?.nodes?.[root.id];
      dataPoint[`resultado_${root.id}`] = nodeData?.result;
    });
    
    return dataPoint;
  }) || [] : chartData;

  // Build consolidated chart data for "Vista General" - shows total result per period
  // IMPORTANT: Use backend's pre-calculated total_result which already respects goal_calculation_type
  // (average for 'average'/'fixed_per_period', sum for 'accumulated')
  const consolidatedChartData = hasMultipleRoots ? analysisData?.periods?.map((p) => {
    const hierarchyPeriod = analysisData?.hierarchy?.periods?.[p.period_id];
    
    // Use backend's pre-calculated totals (respects avg vs sum based on goal_calculation_type)
    // This ensures consistency between Cards, Detail table, and Chart
    const totalResult = hierarchyPeriod?.total_result;
    const totalGoal = hierarchyPeriod?.total_goal;
    
    return {
      name: p.period_label || `PER ${p.period_number}`,
      periodId: p.period_id,
      meta: totalGoal ?? p.goal, // Use hierarchy total_goal, fallback to period goal
      resultado: totalResult ?? null,
    };
  }) || [] : chartData;

  // Determine which chart data to use based on view mode
  const activeChartData = hasMultipleRoots 
    ? (chartViewMode === 'general' ? consolidatedChartData : hierarchicalChartData)
    : chartData;

  const chartDataWithTrend = computeTrendData(activeChartData);

  // Calculate totals using centralized helper
  const calculationType = selectedKPI?.goal_calculation_type || 'accumulated';
  const isHierarchical = selectedKPI?.has_hierarchy && selectedKPI?.hierarchy_nodes?.length > 0;
  const { totalGoal, totalResult, capturedCount, totalPeriods } = calculateKPISummary(
    analysisData?.periods,
    calculationType,
    isHierarchical
  );
  
  // Calculate overall performance and status using centralized helper
  let { performance: overallPerformance, status: overallStatus } = calculateOverallPerformance(
    totalResult,
    totalGoal,
    capturedCount,
    selectedKPI?.evaluation_type
  );

  // Meta por rango: sobrescribir desempeño/estado con evaluación de rango (verde/rojo, sin %)
  const isRangeKPI = calculationType === 'range';
  const summaryRange = isRangeKPI
    ? (analysisData?.periods?.find((p) => p.goal_range)?.goal_range || null)
    : null;
  if (isRangeKPI) {
    const ro = calculateRangeOverall(analysisData?.periods, summaryRange);
    overallPerformance = ro.performance;
    overallStatus = ro.status;
  }

  // Meta por rango: dominio dinámico del eje Y de la gráfica de tendencia.
  // Considera TODOS los rangos por bimestre (staircase) + resultados capturados, con ~18% margen.
  let rangeYDomain = undefined;
  if (isRangeKPI && summaryRange) {
    const vals = [];
    (analysisData?.periods || []).forEach((p) => {
      if (p.goal_range?.min != null) vals.push(p.goal_range.min);
      if (p.goal_range?.max != null) vals.push(p.goal_range.max);
      if (p.result !== null && p.result !== undefined && !isNaN(p.result)) vals.push(p.result);
    });
    if (vals.length === 0) { vals.push(summaryRange.min, summaryRange.max); }
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const span = (hi - lo) || Math.abs(hi) || 1;
    const margin = span * 0.18;
    rangeYDomain = [Math.floor(lo - margin), Math.ceil(hi + margin)];
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="kpi-analysis-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <Button variant="ghost" size="icon" onClick={handleBackToList} className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <UserAvatar
                name={selectedKPI.responsible_name}
                avatarUrl={selectedKPI.responsible_avatar}
                size="xl"
                className="flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope'] truncate uppercase">{selectedKPI.name}</h1>
                <p className="text-slate-500 mt-0.5 text-xs sm:text-sm truncate"><span className="uppercase">{selectedKPI.area}</span> • {selectedKPI.responsible_name}</p>
              </div>
            </div>
            
            {/* KPI Navigation Controls */}
            {kpiList.length > 1 && (
              <TooltipProvider>
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                  {/* Position indicator */}
                  <span className="text-xs text-slate-400 hidden sm:inline mr-1">
                    {currentIndex + 1} / {kpiList.length}
                  </span>
                  
                  {/* Previous KPI button */}
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleNavigateToPrevious}
                        disabled={!hasPrevious}
                        className={cn(
                          "h-9 w-9",
                          !hasPrevious && "opacity-50 cursor-not-allowed"
                        )}
                        data-testid="prev-kpi-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {hasPrevious 
                        ? `Ir al KPI anterior: ${previousKPI?.name}` 
                        : "No hay KPI anterior"}
                    </TooltipContent>
                  </UITooltip>
                  
                  {/* Next KPI button */}
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleNavigateToNext}
                        disabled={!hasNext}
                        className={cn(
                          "h-9 w-9",
                          !hasNext && "opacity-50 cursor-not-allowed"
                        )}
                        data-testid="next-kpi-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {hasNext 
                        ? `Ir al siguiente KPI: ${nextKPI?.name}` 
                        : "No hay más KPIs"}
                    </TooltipContent>
                  </UITooltip>
                </div>
              </TooltipProvider>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 sm:w-32" data-testid="analysis-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Bimester/Quarter selector - only for weekly KPIs */}
            {isWeeklyKPI && (
              <Select value={selectedBimester?.toString() || 'all'} onValueChange={(v) => setSelectedBimester(v === 'all' ? null : parseInt(v))}>
                <SelectTrigger className="w-28 sm:w-40" data-testid="analysis-bimester-select">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Array.from({ length: goalPeriodCount }, (_, i) => i + 1).map((b) => (
                    <SelectItem key={b} value={b.toString()}>
                      {getGoalPeriodName(goalPeriodicity, b)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Periodicity badge - for non-weekly KPIs */}
            {!isWeeklyKPI && (
              <Badge variant="outline" className="text-sm bg-blue-50 text-blue-700 border-blue-200 px-3 py-1.5">
                {periodicityInfo.periodicity_label || periodicityInfo.periodicity}
              </Badge>
            )}
            
            <Button variant="outline" size="sm" onClick={exportToCSV} className="h-9" data-testid="export-csv-btn">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">CSV</span>
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => onEdit(selectedKPI)} className="h-9" data-testid="edit-kpi-btn">
                <Edit className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Editar</span>
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <Card className="card-hover">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">
                    {isRangeKPI ? 'Resultado Promedio' : (calculationType === 'fixed_per_period' ? 'Resultado Promedio' : 'Resultado Total')}
                  </p>
                  <p className="text-xl sm:text-3xl font-bold text-slate-900 font-['Manrope'] mt-1">
                    {formatNumber(totalResult)}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("card-hover", calculationType === 'fixed_per_period' && "border-amber-200 bg-amber-50/30")}>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-xs sm:text-sm font-medium", calculationType === 'fixed_per_period' ? "text-amber-700" : "text-slate-500")}>
                    {isRangeKPI ? 'Rango meta' : (calculationType === 'fixed_per_period' ? 'Límite Fijo' : 'Meta Total')}
                  </p>
                  <p className={cn("text-xl sm:text-3xl font-bold font-['Manrope'] mt-1", calculationType === 'fixed_per_period' ? "text-amber-800" : "text-slate-900")}>
                    {isRangeKPI ? formatGoalRange(summaryRange) : formatNumber(totalGoal)}
                  </p>
                </div>
                <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center", calculationType === 'fixed_per_period' ? "bg-amber-100" : "bg-slate-100")}>
                  <Target className={cn("w-5 h-5 sm:w-6 sm:h-6", calculationType === 'fixed_per_period' ? "text-amber-600" : "text-slate-600")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Desempeño</p>
                  {isRangeKPI && capturedCount === 0 ? (
                    <p data-testid="performance-value" className="text-sm sm:text-base font-semibold text-slate-400 mt-2">
                      Sin datos en este periodo
                    </p>
                  ) : (
                    <p data-testid="performance-value" className="text-xl sm:text-3xl font-bold font-['Manrope'] mt-1" style={{
                      color: overallStatus === 'success' ? '#10b981' : overallStatus === 'error' ? '#ef4444' : '#64748b'
                    }}>
                      {typeof overallPerformance === 'number' ? `${overallPerformance.toFixed(1)}%` : overallPerformance || '-'}
                    </p>
                  )}
                </div>
                <div className={cn('w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center', {
                  'bg-emerald-50': overallStatus === 'success',
                  'bg-rose-50': overallStatus === 'error',
                  'bg-rose-50': overallStatus === 'error',
                  'bg-slate-100': overallStatus === 'pending',
                })}>
                  <TrendingUp className={cn('w-5 h-5 sm:w-6 sm:h-6', {
                    'text-emerald-600': overallStatus === 'success',
                    'text-rose-600': overallStatus === 'error',
                    'text-rose-600': overallStatus === 'error',
                    'text-slate-400': overallStatus === 'pending',
                  })} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Periodos</p>
                  <p className="text-xl sm:text-3xl font-bold text-slate-900 font-['Manrope'] mt-1">
                    {capturedCount} / {totalPeriods}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts and Table */}
        <Tabs defaultValue="chart" className="space-y-4">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="chart" className="text-xs sm:text-sm">Gráfica</TabsTrigger>
            <TabsTrigger value="table" className="text-xs sm:text-sm">Detalle</TabsTrigger>
            {/* Only show Bimester/Quarter tab for weekly KPIs */}
            {isWeeklyKPI && (
              <TabsTrigger value="bimester" className="text-xs sm:text-sm">{getGoalPeriodTermLabel(goalPeriodicity)}</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="chart">
            <div className="space-y-4">
              {/* Area Chart with shaded regions */}
              <Card>
                <CardHeader className="px-3 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="font-['Manrope'] text-base sm:text-lg">
                      Tendencia de Resultados vs Meta
                      {hasMultipleRoots && chartViewMode === 'breakdown' && (
                        <span className="text-xs font-normal text-slate-500 ml-2">
                          (por {selectedKPI?.hierarchy_dimension || 'Padre'})
                        </span>
                      )}
                    </CardTitle>
                    
                    <div className="flex items-center gap-2">
                      {/* Trend toggle */}
                      <Button
                        variant={showTrend ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 text-xs", showTrend && "bg-orange-500 hover:bg-orange-600")}
                        onClick={() => setShowTrend(!showTrend)}
                        data-testid="trend-toggle"
                      >
                        <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                        Tendencia
                      </Button>

                      {/* View mode selector - only for hierarchical KPIs */}
                      {hasMultipleRoots && (
                        <Select value={chartViewMode} onValueChange={handleChartViewChange}>
                          <SelectTrigger className="w-40 h-8 text-xs" data-testid="chart-view-mode">
                            <Layers className="w-3.5 h-3.5 mr-1.5" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">
                              <div className="flex items-center gap-2">
                                <Target className="w-3.5 h-3.5 text-blue-500" />
                                <span>Vista General</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="breakdown">
                              <div className="flex items-center gap-2">
                                <GitBranch className="w-3.5 h-3.5 text-violet-500" />
                                <span>Vista Desglose</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  {hasMultipleRoots && (
                    <p className="text-xs text-slate-500 mt-1">
                      {chartViewMode === 'general' 
                        ? 'Resultado total consolidado del KPI padre'
                        : `Desglose por ${selectedKPI?.hierarchy_dimension || 'nodo'}`}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="px-2 sm:px-6">
                  <div className="h-64 sm:h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart 
                        data={chartDataWithTrend} 
                        margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                        onMouseMove={(e) => {
                          if (e && e.activePayload && e.activePayload[0]) {
                            setHighlightedPeriod(e.activePayload[0].payload.periodId);
                          }
                        }}
                        onMouseLeave={() => setHighlightedPeriod(null)}
                      >
                        <defs>
                          {/* Gradient for Meta area - neutral gray */}
                          <linearGradient id="metaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#94a3b8" stopOpacity={0.05}/>
                          </linearGradient>
                          {/* Gradient for Resultado area - dynamic based on status */}
                          <linearGradient id="resultadoGradientSuccess" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="resultadoGradientError" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="resultadoGradientPending" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#64748b" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#64748b" stopOpacity={0.05}/>
                          </linearGradient>
                          {/* Gradient for Resultado Total - fixed blue (no compliance color) */}
                          <linearGradient id="resultadoGradientBlue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#2563EB" stopOpacity={0.05}/>
                          </linearGradient>
                          {/* Gradients for parent series */}
                          {rootNodes.map((root, idx) => (
                            <linearGradient key={root.id} id={`parentGradient_${root.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={parentColors[idx % parentColors.length].fill} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={parentColors[idx % parentColors.length].fill} stopOpacity={0.05}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={10} tick={{ fontSize: 10 }} />
                        <YAxis 
                          stroke="#64748b" 
                          fontSize={10} 
                          tick={{ fontSize: 10 }} 
                          width={55}
                          domain={isRangeKPI && rangeYDomain ? rangeYDomain : undefined}
                          allowDecimals={false}
                          tickFormatter={formatAxisNumber}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(value, name) => [formatNumber(value), name]}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        
                        {/* Meta area - rendered first (bottom layer) - as reference line for fixed goal */}
                        {isRangeKPI ? (
                          <>
                            <Line
                              type="linear"
                              dataKey="rangeMin"
                              stroke="#059669"
                              strokeWidth={1.5}
                              strokeDasharray="5 5"
                              name="Mín rango"
                              dot={false}
                              activeDot={false}
                              connectNulls
                              isAnimationActive={false}
                            />
                            <Line
                              type="linear"
                              dataKey="rangeMax"
                              stroke="#059669"
                              strokeWidth={1.5}
                              strokeDasharray="5 5"
                              name="Máx rango"
                              dot={false}
                              activeDot={false}
                              connectNulls
                              isAnimationActive={false}
                            />
                          </>
                        ) : (
                          <Area 
                            type="monotone" 
                            dataKey="meta" 
                            stroke="#94a3b8" 
                            strokeWidth={2}
                            strokeDasharray={(hasMultipleRoots && chartViewMode === 'breakdown') ? "5 5" : "0"}
                            fill="url(#metaGradient)"
                            name="Meta (Límite)"
                            dot={{ fill: '#94a3b8', r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, strokeWidth: 0 }}
                          />
                        )}
                        
                        {/* For hierarchical KPIs with multiple roots */}
                        {hasMultipleRoots ? (
                          chartViewMode === 'breakdown' ? (
                            // Vista Desglose: show line per parent node
                            rootNodes.map((root, idx) => (
                              <Line
                                key={root.id}
                                type="monotone"
                                dataKey={`resultado_${root.id}`}
                                stroke={parentColors[idx % parentColors.length].stroke}
                                strokeWidth={2}
                                name={root.name}
                                dot={{ fill: parentColors[idx % parentColors.length].fill, r: 4, strokeWidth: 0 }}
                                activeDot={{ r: 6, strokeWidth: 0 }}
                                connectNulls
                              />
                            ))
                          ) : (
                            // Vista General: show consolidated total result
                            <Area 
                              type="monotone" 
                              dataKey="resultado" 
                              stroke="#2563EB"
                              strokeWidth={2}
                              fill="url(#resultadoGradientBlue)"
                              name="Resultado Total"
                              dot={{ fill: '#2563EB', r: 4, strokeWidth: 0 }}
                              activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                          )
                        ) : (
                          /* Single resultado area for non-hierarchical or single-root KPIs */
                          <Area 
                            type="monotone" 
                            dataKey="resultado" 
                            stroke="#2563EB"
                            strokeWidth={2}
                            fill="url(#resultadoGradientBlue)"
                            name="Resultado"
                            dot={{ fill: '#2563EB', r: 4, strokeWidth: 0 }}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                          />
                        )}

                        {/* Trend line (linear regression) */}
                        {showTrend && (
                          <Line
                            type="linear"
                            dataKey="tendencia"
                            stroke="#f97316"
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            name="Tendencia"
                            dot={false}
                            activeDot={false}
                            connectNulls
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Analysis Table */}
              <Card data-testid="analysis-table-card">
                <CardHeader className="px-3 sm:px-6 pb-2">
                  <CardTitle className="font-['Manrope'] text-base sm:text-lg flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                    Tabla de Análisis
                    {selectedKPI?.has_hierarchy && (
                      <Badge variant="outline" className="text-xs ml-2">
                        <GitBranch className="w-3 h-3 mr-1" />
                        {selectedKPI.hierarchy_dimension || 'Jerárquico'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Desktop Analysis Table */}
                  <div className="table-responsive hidden sm:block">
                    <table className="data-table" data-testid="analysis-table">
                      <thead>
                        <tr>
                          <th>{isWeeklyKPI ? 'PER' : (periodicityInfo.periodicity_label || 'Periodo')}</th>
                          {selectedKPI?.has_hierarchy && <th>{selectedKPI.hierarchy_dimension || 'Desglose'}</th>}
                          <th className="text-right">Meta</th>
                          <th className="text-right">Resultado</th>
                          <th className="text-right">Δ (Diferencia)</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisData?.periods?.map((period) => {
                          const delta = period.result !== null ? period.result - period.goal : null;
                          const isHighlighted = highlightedPeriod === period.period_id;
                          const deltaClass = delta === null ? 'text-slate-400' : 
                            (selectedKPI?.evaluation_type === 'percentage' 
                              ? (delta >= 0 ? 'text-emerald-600' : 'text-rose-600')
                              : (delta <= 0 ? 'text-emerald-600' : 'text-rose-600'));

                          // Meta por rango: diferencia = distancia al límite más cercano
                          // (0 si está dentro; negativo si por debajo del mín; positivo si por encima del máx)
                          let rangeDelta = null;
                          if (isRangeKPI && period.result !== null && period.result !== undefined) {
                            const gr = period.goal_range || summaryRange;
                            if (gr && gr.min !== null && gr.min !== undefined && gr.max !== null && gr.max !== undefined) {
                              if (period.result < gr.min) rangeDelta = period.result - gr.min;
                              else if (period.result > gr.max) rangeDelta = period.result - gr.max;
                              else rangeDelta = 0;
                            }
                          }
                          const rangeDeltaClass = rangeDelta === null ? 'text-slate-400'
                            : (rangeDelta === 0 ? 'text-emerald-600' : 'text-rose-600');
                          
                          const isExpanded = expandedNodes[period.period_id];
                          const hierarchyData = analysisData?.hierarchy?.periods?.[period.period_id];
                          const hasHierarchy = selectedKPI?.has_hierarchy && hierarchyData;
                          const rootNodes = selectedKPI?.hierarchy_nodes?.filter(n => !n.parent_id) || [];
                          
                          return (
                            <React.Fragment key={period.period_id}>
                              <tr 
                                className={cn(
                                  'transition-colors cursor-pointer',
                                  isHighlighted && 'bg-emerald-50'
                                )}
                                onMouseEnter={() => setHighlightedPeriod(period.period_id)}
                                onMouseLeave={() => setHighlightedPeriod(null)}
                                onClick={() => hasHierarchy && setExpandedNodes({...expandedNodes, [period.period_id]: !isExpanded})}
                                data-testid={`analysis-row-${period.period_number}`}
                              >
                                <td className="font-medium">
                                  <div className="flex items-center gap-1">
                                    {hasHierarchy && (
                                      isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
                                    )}
                                    {period.period_label || `PER ${period.period_number}`}
                                    {period.is_special && <span className="ml-1 text-xs text-amber-600">(E)</span>}
                                  </div>
                                </td>
                                {selectedKPI?.has_hierarchy && (
                                  <td className="text-slate-500 text-sm">
                                    {hasHierarchy ? 'TOTAL' : '-'}
                                  </td>
                                )}
                                {isRangeKPI ? (
                                  <td className="text-right">{formatGoalRange(period.goal_range || summaryRange)}</td>
                                ) : (
                                  <GoalCell value={period.goal} />
                                )}
                                <ResultCell value={period.result} />
                                <td className={cn('text-right font-medium', isRangeKPI ? rangeDeltaClass : deltaClass)}>
                                  {isRangeKPI
                                    ? (rangeDelta === null ? '-' : (rangeDelta === 0 ? '0' : `${rangeDelta > 0 ? '+' : ''}${formatNumber(rangeDelta)}`))
                                    : (delta !== null ? (
                                        <>
                                          {delta > 0 && '+'}
                                          {formatNumber(delta)}
                                        </>
                                      ) : '-')}
                                </td>
                                <td className="text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <StatusBadge status={period.status} isRange={isRangeKPI} />
                                    {/* Comment history button with badge */}
                                    {(() => {
                                      const commentCount = getCommentCount(period.period_id);
                                      return (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenCommentHistory(period.period_id, period.period_number);
                                          }}
                                          className={cn(
                                            'relative p-1 rounded-md transition-colors',
                                            commentCount > 0 
                                              ? 'text-blue-600 hover:bg-blue-50' 
                                              : 'text-slate-300 hover:bg-slate-50 hover:text-slate-500'
                                          )}
                                          title={commentCount > 0 ? `Ver ${commentCount} comentario(s)` : 'Sin comentarios'}
                                          data-testid={`comment-btn-${period.period_number}`}
                                        >
                                          <MessageCircle className={cn(
                                            'w-4 h-4',
                                            commentCount > 0 && 'fill-blue-100'
                                          )} />
                                          {commentCount > 0 && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                                              {commentCount > 9 ? '9+' : commentCount}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })()}
                                  </div>
                                </td>
                              </tr>
                              
                              {/* Expanded hierarchy rows */}
                              {hasHierarchy && isExpanded && rootNodes.map((node) => {
                                const nodeData = hierarchyData?.nodes?.[node.id];
                                const nodeStatus = calculateKPIStatus(nodeData?.result, nodeData?.goal, selectedKPI?.evaluation_type);
                                const nodeDelta = nodeData?.result !== null && nodeData?.result !== undefined 
                                  ? nodeData.result - (nodeData?.goal || 0) 
                                  : null;
                                const nodeDeltaClass = nodeDelta === null ? 'text-slate-400' : 
                                  (selectedKPI?.evaluation_type === 'percentage' 
                                    ? (nodeDelta >= 0 ? 'text-emerald-600' : 'text-rose-600')
                                    : (nodeDelta <= 0 ? 'text-emerald-600' : 'text-rose-600'));
                                const children = selectedKPI?.hierarchy_nodes?.filter(n => n.parent_id === node.id) || [];
                                
                                return (
                                  <React.Fragment key={`${period.period_id}-${node.id}`}>
                                    <tr className="bg-slate-50/50">
                                      <td></td>
                                      <td className="font-medium text-sm">
                                        <div className="flex items-center gap-2 pl-2">
                                          <Layers className="w-3 h-3 text-slate-400" />
                                          {node.name}
                                          {!node.is_leaf && <span className="text-xs text-slate-400 ml-1">(calc)</span>}
                                          {/* Node status badge */}
                                          <NodeStatusBadge 
                                            result={nodeData?.result} 
                                            goal={nodeData?.goal} 
                                            evaluationType={selectedKPI?.evaluation_type}
                                            compact
                                          />
                                        </div>
                                      </td>
                                      <GoalCell value={nodeData?.goal || 0} className="text-sm" />
                                      <ResultCell value={nodeData?.result} className="text-sm" />
                                      <td className={cn('text-right text-sm', nodeDeltaClass)}>
                                        {nodeDelta !== null ? (
                                          <>
                                            {nodeDelta > 0 && '+'}
                                            {formatNumber(nodeDelta)}
                                          </>
                                        ) : '-'}
                                      </td>
                                      <td className="text-center">
                                        {/* Parent node - read-only comment view */}
                                        {(() => {
                                          const nodeCommentCount = getCommentCount(period.period_id, node.id);
                                          return (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenCommentHistory(period.period_id, period.period_number, node.id, node.name, false);
                                              }}
                                              className={cn(
                                                'relative p-1 rounded-md transition-colors',
                                                nodeCommentCount > 0 
                                                  ? 'text-blue-600 hover:bg-blue-50' 
                                                  : 'text-slate-300 hover:bg-slate-50 hover:text-slate-500'
                                              )}
                                              title={nodeCommentCount > 0 ? `Ver ${nodeCommentCount} comentario(s)` : 'Agregar comentario'}
                                              data-testid={`node-comment-btn-${period.period_number}-${node.id}`}
                                            >
                                              <MessageCircle className={cn(
                                                'w-3.5 h-3.5',
                                                nodeCommentCount > 0 && 'fill-blue-100'
                                              )} />
                                              {nodeCommentCount > 0 && (
                                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-600 text-white text-[9px] font-medium rounded-full flex items-center justify-center">
                                                  {nodeCommentCount > 9 ? '9+' : nodeCommentCount}
                                                </span>
                                              )}
                                            </button>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                    
                                    {/* Child nodes (leaf level) */}
                                    {children.map((child) => {
                                      const childData = hierarchyData?.nodes?.[child.id];
                                      const childStatus = calculateKPIStatus(childData?.result, childData?.goal, selectedKPI?.evaluation_type);
                                      const childDelta = childData?.result !== null && childData?.result !== undefined 
                                        ? childData.result - (childData?.goal || 0) 
                                        : null;
                                      const childDeltaClass = childDelta === null ? 'text-slate-400' : 
                                        (selectedKPI?.evaluation_type === 'percentage' 
                                          ? (childDelta >= 0 ? 'text-emerald-600' : 'text-rose-600')
                                          : (childDelta <= 0 ? 'text-emerald-600' : 'text-rose-600'));
                                      
                                      return (
                                        <tr key={`${period.period_id}-${child.id}`} className="bg-slate-50/30">
                                          <td></td>
                                          <td className="text-sm text-slate-600">
                                            <div className="flex items-center gap-2 pl-6">
                                              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                              {child.name}
                                              {/* Child node status badge */}
                                              <NodeStatusBadge 
                                                result={childData?.result} 
                                                goal={childData?.goal} 
                                                evaluationType={selectedKPI?.evaluation_type}
                                                compact
                                              />
                                            </div>
                                          </td>
                                          <GoalCell value={childData?.goal || 0} className="text-sm text-slate-600" />
                                          <ResultCell value={childData?.result} className="text-sm" />
                                          <td className={cn('text-right text-sm', childDeltaClass)}>
                                            {childDelta !== null ? (
                                              <>
                                                {childDelta > 0 && '+'}
                                                {formatNumber(childDelta)}
                                              </>
                                            ) : '-'}
                                          </td>
                                          <td className="text-center">
                                            {/* Leaf node - can add comments */}
                                            {(() => {
                                              const childCommentCount = getCommentCount(period.period_id, child.id);
                                              return (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenCommentHistory(period.period_id, period.period_number, child.id, child.name, true);
                                                  }}
                                                  className={cn(
                                                    'relative p-1 rounded-md transition-colors',
                                                    childCommentCount > 0 
                                                      ? 'text-blue-600 hover:bg-blue-50' 
                                                      : 'text-slate-300 hover:bg-slate-50 hover:text-slate-500'
                                                  )}
                                                  title={childCommentCount > 0 ? `Ver ${childCommentCount} comentario(s)` : 'Agregar comentario'}
                                                  data-testid={`node-comment-btn-${period.period_number}-${child.id}`}
                                                >
                                                  <MessageCircle className={cn(
                                                    'w-3.5 h-3.5',
                                                    childCommentCount > 0 && 'fill-blue-100'
                                                  )} />
                                                  {childCommentCount > 0 && (
                                                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-600 text-white text-[9px] font-medium rounded-full flex items-center justify-center">
                                                      {childCommentCount > 9 ? '9+' : childCommentCount}
                                                    </span>
                                                  )}
                                                </button>
                                              );
                                            })()}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      {/* Footer - For fixed_per_period, show PROMEDIO with fixed limit indicator */}
                      {isRangeKPI ? (
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr className="font-semibold">
                            <td>PROMEDIO</td>
                            {selectedKPI?.has_hierarchy && <td></td>}
                            <td className="text-right">{formatGoalRange(summaryRange)}</td>
                            <ResultCell value={totalResult} isTotal />
                            <td className="text-right text-slate-400">-</td>
                            <td className="text-center"><StatusBadge status={overallStatus} isRange={isRangeKPI} /></td>
                          </tr>
                        </tfoot>
                      ) : calculationType === 'fixed_per_period' ? (
                        <tfoot className="bg-amber-50/50 border-t-2 border-amber-200">
                          <tr className="font-semibold">
                            <td className="text-amber-800">PROMEDIO</td>
                            {selectedKPI?.has_hierarchy && <td></td>}
                            <td className="text-center text-amber-700">
                              <span className="text-xs font-normal">(Límite fijo)</span>
                              <br />{formatNumber(totalGoal)}
                            </td>
                            <td className="text-right text-amber-800">{formatNumber(totalResult)}</td>
                            <td className={cn(
                              'text-right',
                              selectedKPI?.evaluation_type === 'percentage'
                                ? (totalResult >= totalGoal ? 'text-emerald-600' : 'text-rose-600')
                                : (totalResult <= totalGoal ? 'text-emerald-600' : 'text-rose-600')
                            )}>
                              Δ {formatNumber(totalGoal - totalResult)}
                            </td>
                            <td className="text-center"><StatusBadge status={overallStatus} isRange={isRangeKPI} /></td>
                          </tr>
                        </tfoot>
                      ) : (
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr className="font-semibold">
                            <td>TOTAL</td>
                            {selectedKPI?.has_hierarchy && <td></td>}
                            <GoalCell value={totalGoal} isTotal />
                            <ResultCell value={totalResult} isTotal />
                            <td className={cn(
                              'text-right',
                              selectedKPI?.evaluation_type === 'percentage'
                                ? (totalResult - totalGoal >= 0 ? 'text-emerald-600' : 'text-rose-600')
                                : (totalResult - totalGoal <= 0 ? 'text-emerald-600' : 'text-rose-600')
                            )}>
                              {totalResult - totalGoal > 0 && '+'}
                              {formatNumber(totalResult - totalGoal)}
                            </td>
                            <td className="text-center"><StatusBadge status={overallStatus} isRange={isRangeKPI} /></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  {/* Mobile Analysis Cards */}
                  <div className="sm:hidden">
                    <div className="divide-y divide-slate-100">
                      {analysisData?.periods?.map((period) => {
                        const delta = period.result !== null ? period.result - period.goal : null;
                        const isHighlighted = highlightedPeriod === period.period_id;
                        const deltaClass = delta === null ? 'text-slate-400' : 
                          (selectedKPI?.evaluation_type === 'percentage' 
                            ? (delta >= 0 ? 'text-emerald-600' : 'text-rose-600')
                            : (delta <= 0 ? 'text-emerald-600' : 'text-rose-600'));

                        // Meta por rango: diferencia = distancia al límite más cercano
                        let rangeDelta = null;
                        if (isRangeKPI && period.result !== null && period.result !== undefined) {
                          const gr = period.goal_range || summaryRange;
                          if (gr && gr.min !== null && gr.min !== undefined && gr.max !== null && gr.max !== undefined) {
                            if (period.result < gr.min) rangeDelta = period.result - gr.min;
                            else if (period.result > gr.max) rangeDelta = period.result - gr.max;
                            else rangeDelta = 0;
                          }
                        }
                        const rangeDeltaClass = rangeDelta === null ? 'text-slate-400'
                          : (rangeDelta === 0 ? 'text-emerald-600' : 'text-rose-600');
                        
                        return (
                          <div 
                            key={period.period_id}
                            className={cn(
                              'p-3 transition-colors',
                              isHighlighted && 'bg-emerald-50'
                            )}
                            onClick={() => setHighlightedPeriod(isHighlighted ? null : period.period_id)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-slate-900">
                                {period.period_label || `PER ${period.period_number}`}
                                {period.is_special && <span className="ml-1 text-xs text-amber-600">(E)</span>}
                              </span>
                              <StatusBadge status={period.status} isRange={isRangeKPI} />
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div>
                                <p className="text-slate-500 text-xs">{isRangeKPI ? 'Rango' : 'Meta'}</p>
                                <p className="font-medium">{isRangeKPI ? formatGoalRange(period.goal_range || summaryRange) : formatNumber(period.goal)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">Resultado</p>
                                <p className="font-medium">{period.result !== null ? formatNumber(period.result) : '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 text-xs">Δ</p>
                                <p className={cn('font-medium', isRangeKPI ? rangeDeltaClass : deltaClass)}>
                                  {isRangeKPI
                                    ? (rangeDelta === null ? '-' : (rangeDelta === 0 ? '0' : `${rangeDelta > 0 ? '+' : ''}${formatNumber(rangeDelta)}`))
                                    : (delta !== null ? (
                                        <>
                                          {delta > 0 && '+'}
                                          {formatNumber(delta)}
                                        </>
                                      ) : '-')}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Mobile Total Row */}
                    {isRangeKPI ? (
                      <div className="p-3 bg-slate-50 border-t-2 border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-900">PROMEDIO</span>
                          <StatusBadge status={overallStatus} isRange={isRangeKPI} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500 text-xs">Rango</p>
                            <p className="font-semibold">{formatGoalRange(summaryRange)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Resultado prom.</p>
                            <p className="font-semibold">{formatNumber(totalResult)}</p>
                          </div>
                        </div>
                      </div>
                    ) : calculationType === 'fixed_per_period' ? (
                      <div className="p-3 bg-amber-50/50 border-t-2 border-amber-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-amber-800">PROMEDIO</span>
                          <StatusBadge status={overallStatus} isRange={isRangeKPI} />
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-amber-600 text-xs">Límite fijo</p>
                            <p className="font-semibold text-amber-800">{formatNumber(totalGoal)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Promedio</p>
                            <p className="font-semibold">{formatNumber(totalResult)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Δ</p>
                            <p className={cn(
                              'font-semibold',
                              selectedKPI?.evaluation_type === 'percentage'
                                ? (totalResult >= totalGoal ? 'text-emerald-600' : 'text-rose-600')
                                : (totalResult <= totalGoal ? 'text-emerald-600' : 'text-rose-600')
                            )}>
                              {formatNumber(totalGoal - totalResult)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-slate-50 border-t-2 border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-900">TOTAL</span>
                          <StatusBadge status={overallStatus} isRange={isRangeKPI} />
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500 text-xs">Meta</p>
                            <p className="font-semibold">{formatNumber(totalGoal)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Resultado</p>
                            <p className="font-semibold">{formatNumber(totalResult)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Δ</p>
                            <p className={cn(
                              'font-semibold',
                              selectedKPI?.evaluation_type === 'percentage'
                                ? (totalResult - totalGoal >= 0 ? 'text-emerald-600' : 'text-rose-600')
                                : (totalResult - totalGoal <= 0 ? 'text-emerald-600' : 'text-rose-600')
                            )}>
                              {totalResult - totalGoal > 0 && '+'}
                              {formatNumber(totalResult - totalGoal)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="table">
            <Card>
              <CardContent className="p-0">
                {/* Desktop Table */}
                <div className="table-responsive hidden sm:block">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Periodo</th>
                        <th className="hidden md:table-cell">Trimestre</th>
                        <th className="hidden lg:table-cell">Fechas</th>
                        <th className="text-right">Meta</th>
                        <th className="text-right">Resultado</th>
                        <th className="text-right hidden md:table-cell">Desempeño</th>
                        <th className="text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData?.periods?.map((period) => (
                        <tr key={period.period_id}>
                          <td className="font-medium">{period.period_label || `PER ${period.period_number}`}</td>
                          <td className="hidden md:table-cell">{period.bimester_name}</td>
                          <td className="text-slate-500 hidden lg:table-cell">{period.period_dates}</td>
                          {isRangeKPI ? (
                            <td className="text-right">{formatGoalRange(period.goal_range || summaryRange)}</td>
                          ) : (
                            <GoalCell value={period.goal} />
                          )}
                          <ResultCell value={period.result} />
                          <td className="text-right hidden md:table-cell">
                            {typeof period.performance === 'number' ? `${period.performance}%` : period.performance || '-'}
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <StatusBadge status={period.status} isRange={isRangeKPI} />
                              {(() => {
                                const commentCount = getCommentCount(period.period_id);
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenCommentHistory(period.period_id, period.period_number);
                                    }}
                                    className={cn(
                                      'relative p-1 rounded-md transition-colors',
                                      commentCount > 0 
                                        ? 'text-blue-600 hover:bg-blue-50' 
                                        : 'text-slate-300 hover:bg-slate-50 hover:text-slate-500'
                                    )}
                                    title={commentCount > 0 ? `Ver ${commentCount} comentario(s)` : 'Sin comentarios'}
                                    data-testid={`detail-comment-btn-${period.period_number}`}
                                  >
                                    <MessageCircle className={cn(
                                      'w-4 h-4',
                                      commentCount > 0 && 'fill-blue-100'
                                    )} />
                                    {commentCount > 0 && (
                                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                                        {commentCount > 9 ? '9+' : commentCount}
                                      </span>
                                    )}
                                  </button>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Card List */}
                <div className="sm:hidden divide-y divide-slate-100">
                  {analysisData?.periods?.map((period) => {
                    const commentCount = getCommentCount(period.period_id);
                    return (
                      <div key={period.period_id} className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{period.period_label || `PER ${period.period_number}`}</span>
                          <div className="flex items-center gap-1">
                            <StatusBadge status={period.status} isRange={isRangeKPI} />
                            <button
                              onClick={() => handleOpenCommentHistory(period.period_id, period.period_number)}
                              className={cn(
                                'relative p-1 rounded-md transition-colors',
                                commentCount > 0 
                                  ? 'text-blue-600 hover:bg-blue-50' 
                                  : 'text-slate-300 hover:bg-slate-50 hover:text-slate-500'
                              )}
                            >
                              <MessageCircle className={cn('w-4 h-4', commentCount > 0 && 'fill-blue-100')} />
                              {commentCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                                  {commentCount > 9 ? '9+' : commentCount}
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500 text-xs">{isRangeKPI ? 'Rango' : 'Meta'}</p>
                            <p className="font-medium">{isRangeKPI ? formatGoalRange(period.goal_range || summaryRange) : formatNumber(period.goal)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Resultado</p>
                            <p className="font-medium">{period.result !== null ? formatNumber(period.result) : '-'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bimester Tab - only rendered for weekly KPIs */}
          {isWeeklyKPI && (
          <TabsContent value="bimester">
            <Card>
              <CardContent className="pt-4 sm:pt-6 px-2 sm:px-6">
                <div className="h-48 sm:h-64 mb-4 sm:mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analysisData?.bimesters?.map((b) => ({
                        name: b.name.substring(0, 3),
                        meta: b.total_goal,
                        resultado: b.total_result,
                      }))}
                      margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={10} tick={{ fontSize: 10 }} />
                      <YAxis 
                        stroke="#64748b" 
                        fontSize={10} 
                        tick={{ fontSize: 10 }} 
                        width={55}
                        tickFormatter={formatAxisNumber}
                      />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} content={isRangeKPI ? () => (
                        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 12, paddingTop: 4 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 10, height: 10, background: '#10b981', display: 'inline-block', borderRadius: 2 }} />En rango
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 10, height: 10, background: '#ef4444', display: 'inline-block', borderRadius: 2 }} />Fuera de rango
                          </span>
                        </div>
                      ) : undefined} />
                      {!isRangeKPI && <Bar dataKey="meta" fill="#cbd5e1" name="Meta" radius={[4, 4, 0, 0]} />}
                      <Bar dataKey="resultado" fill="#10b981" name="Resultado" radius={[4, 4, 0, 0]}>
                        {isRangeKPI && analysisData?.bimesters?.map((b, i) => (
                          <Cell key={i} fill={b.status === 'error' ? '#ef4444' : b.status === 'success' ? '#10b981' : '#94a3b8'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                  {analysisData?.bimesters?.map((bimester) => (
                    <Card key={bimester.bimester} className="border border-slate-200">
                      <CardContent className="p-2 sm:p-4">
                        <div className="flex items-center justify-between mb-1 sm:mb-2 gap-1">
                          <span className="font-semibold text-slate-900 text-xs sm:text-sm truncate">{bimester.name}</span>
                          <span className={cn('px-1.5 py-0.5 rounded-full text-xs font-medium border flex-shrink-0', {
                            'bg-emerald-50 text-emerald-700 border-emerald-200': bimester.status === 'success',
                            'bg-rose-50 text-rose-700 border-rose-200': bimester.status === 'error',
                            'bg-slate-100 text-slate-600 border-slate-200': !bimester.status || bimester.status === 'pending',
                          })}>
                            {bimester.status === 'success' ? 'OK' : bimester.status === 'error' ? 'X' : '-'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs sm:text-sm">
                          <div>
                            <p className="text-slate-500 text-xs">{isRangeKPI ? 'Rango' : 'Meta'}</p>
                            <p className="font-medium">{isRangeKPI ? (bimester.goal_range ? formatGoalRange(bimester.goal_range) : '-') : formatNumber(bimester.total_goal)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs">Resultado</p>
                            <p className="font-medium">{formatNumber(bimester.total_result)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>

        {/* Comment History Drawer */}
        <Sheet open={commentDrawerOpen} onOpenChange={handleDrawerOpenChange}>
          <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
            <SheetHeader className="flex-shrink-0">
              <SheetTitle className="font-['Manrope'] flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                Comentarios del Periodo
              </SheetTitle>
              <SheetDescription asChild>
                <div className="space-y-2">
                  <div className="font-medium text-slate-700">{selectedKPI?.name}</div>
                  <div className="text-slate-500">
                    PER {commentDrawerData.periodNumber}
                    {commentDrawerData.nodeName && (
                      <span className="ml-2">• {commentDrawerData.nodeName}</span>
                    )}
                  </div>
                  
                  {/* Period Result and Goal */}
                  {(() => {
                    const periodData = getPeriodData(commentDrawerData.periodId);
                    if (!periodData) return null;
                    
                    return (
                      <div className="flex items-center gap-4 mt-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-500">Meta:</span>
                          <span className="text-sm font-semibold text-slate-700">
                            {formatNumber(periodData.goal)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-500">Resultado:</span>
                          <span className={cn(
                            "text-sm font-semibold",
                            periodData.result != null ? "text-slate-700" : "text-slate-400"
                          )}>
                            {periodData.result != null ? formatNumber(periodData.result) : 'Sin capturar'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </SheetDescription>
            </SheetHeader>
            
            {/* Comment History - Scrollable area */}
            <div className="flex-1 overflow-hidden mt-4">
              {commentDrawerData.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : commentDrawerData.comments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-slate-500 font-medium text-sm">Sin comentarios</p>
                  <p className="text-slate-400 text-xs mt-1">
                    Agrega el primer comentario para este periodo.
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                      Historial ({commentDrawerData.comments.length})
                    </p>
                    {commentDrawerData.comments.map((comment) => {
                      const labelConfig = COMMENT_LABELS[comment.label] || COMMENT_LABELS.informativo;
                      const LabelIcon = labelConfig.icon;
                      const commentType = comment.type || 'comment';
                      const typeConfig = COMMENT_TYPES[commentType] || COMMENT_TYPES.comment;
                      const TypeIcon = typeConfig.icon;
                      const isCorrection = commentType === 'result_correction' || commentType === 'goal_correction';
                      const isCancelled = comment.estado === 'cancelado';
                      
                      return (
                        <div 
                          key={comment.id} 
                          className={cn(
                            "border rounded-lg p-3 bg-white shadow-sm",
                            isCorrection && "border-l-4",
                            commentType === 'result_correction' && "border-l-orange-400",
                            commentType === 'goal_correction' && "border-l-purple-400",
                            isCancelled && "bg-slate-50"
                          )}
                          data-testid={`comment-item-${comment.id}`}
                        >
                          {/* Type indicator for corrections */}
                          {isCorrection && (
                            <div className={cn(
                              "flex items-center gap-1.5 text-xs font-medium mb-2",
                              typeConfig.color
                            )}>
                              <TypeIcon className="w-3.5 h-3.5" />
                              <span>{typeConfig.label}</span>
                            </div>
                          )}
                          
                          {/* Comment header */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className={cn(
                              'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border',
                              labelConfig.color
                            )}>
                              <LabelIcon className="w-3 h-3" />
                              {labelConfig.label}
                            </span>
                            <span className="text-xs text-slate-400 whitespace-nowrap">
                              {formatDate(comment.created_at.split('T')[0])}
                            </span>
                          </div>
                          
                          {/* Value change indicator for corrections */}
                          {isCorrection && comment.previous_value != null && comment.new_value != null && (
                            <div className="flex items-center gap-2 text-xs mb-2 bg-slate-50 rounded px-2 py-1">
                              <span className="text-slate-500">
                                {commentType === 'result_correction' ? 'Resultado:' : 'Meta:'}
                              </span>
                              <span className="text-rose-600 line-through">
                                {formatNumber(comment.previous_value)}
                              </span>
                              <span className="text-slate-400">→</span>
                              <span className="text-emerald-600 font-medium">
                                {formatNumber(comment.new_value)}
                              </span>
                            </div>
                          )}
                          
                          {/* Comment text */}
                          <p className={cn(
                            "text-sm text-slate-700 mb-2 whitespace-pre-wrap",
                            isCancelled && "line-through opacity-60"
                          )}>
                            {comment.text}
                          </p>

                          {/* Cancellation note */}
                          {isCancelled && comment.cancelado_por && (
                            <p className="text-xs text-rose-500 italic mb-2" data-testid={`comment-cancelled-note-${comment.id}`}>
                              Anulado por {comment.cancelado_por.usuario}
                              {comment.cancelado_por.fecha && ` · ${formatDate(comment.cancelado_por.fecha.split('T')[0])}`}
                            </p>
                          )}
                          
                          {/* Comment footer - user info + admin cancel action */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <User className="w-3 h-3" />
                              <span>{comment.created_by_name}</span>
                            </div>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleToggleCancel(comment)}
                                disabled={togglingId === comment.id}
                                className={cn(
                                  "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors disabled:opacity-50",
                                  isCancelled
                                    ? "text-emerald-600 hover:bg-emerald-50"
                                    : "text-rose-500 hover:bg-rose-50"
                                )}
                                data-testid={`comment-toggle-cancel-${comment.id}`}
                                title={isCancelled ? 'Reactivar comentario' : 'Anular comentario'}
                              >
                                {isCancelled ? <Undo2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                {isCancelled ? 'Reactivar' : 'Anular'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
            
            {/* Add Comment Form - Fixed at bottom. Enabled for leaf AND parent nodes,
                gated by the period's edit deadline (grace period). Admin can always comment. */}
            {(() => {
              const drawerPeriod = getPeriodData(commentDrawerData.periodId);
              const canComment = isPeriodEditable(drawerPeriod, isAdmin);

              if (!canComment) {
                return (
                  <div className="flex-shrink-0 border-t pt-4 mt-4" data-testid="comment-form-locked">
                    <div className="bg-slate-50 rounded-lg p-3 flex items-start gap-2">
                      <Lock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-500">
                        Este periodo ya cerró su plazo de edición. No es posible agregar nuevos comentarios.
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="flex-shrink-0 border-t pt-4 mt-4 space-y-3">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Agregar Comentario
                  </p>

                  {/* Label selector */}
                  <Select
                    value={newCommentLabel}
                    onValueChange={setNewCommentLabel}
                  >
                    <SelectTrigger className="w-full h-9 text-sm" data-testid="new-comment-label-select">
                      <SelectValue placeholder="Seleccionar etiqueta" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(COMMENT_LABELS).map(([key, config]) => {
                        const LabelIcon = config.icon;
                        return (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              <LabelIcon className="w-3.5 h-3.5" />
                              {config.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>

                  {/* Comment textarea (Enter guarda, Shift+Enter salto de línea) */}
                  <textarea
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!addingComment && newCommentText.trim()) handleAddComment();
                      }
                    }}
                    placeholder="Escribe tu comentario aquí... (Enter para guardar, Shift+Enter para salto de línea)"
                    className="w-full h-20 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="new-comment-textarea"
                  />

                  {/* Submit button */}
                  <Button
                    onClick={handleAddComment}
                    disabled={addingComment || !newCommentText.trim()}
                    className="w-full"
                    data-testid="add-comment-btn"
                  >
                    {addingComment ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Agregando...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Agregar Comentario
                      </>
                    )}
                  </Button>
                </div>
              );
            })()}
          </SheetContent>
        </Sheet>

        {/* Confirmación: comentario sin guardar al cerrar el panel */}
        <Dialog open={closeConfirmOpen} onOpenChange={(o) => { if (!o) setCloseConfirmOpen(false); }}>
          <DialogContent data-testid="drawer-unsaved-comment-dialog">
            <DialogHeader>
              <DialogTitle>Comentario sin guardar</DialogTitle>
              <DialogDescription>
                Tienes un comentario escrito que no has guardado. Si cierras el panel se perderá. ¿Deseas continuar sin guardarlo?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseConfirmOpen(false)} data-testid="drawer-unsaved-cancel">
                Seguir editando
              </Button>
              <Button variant="destructive" onClick={confirmDrawerClose} data-testid="drawer-unsaved-continue">
                Cerrar sin guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default KPIDetailView;
